import { URLList } from "@/data/url_list";
import { FetchApiClientMethod } from "@sadtx/fetch-api-client";
import { errorConverter, SATError, SATResult } from "@sadtx/sat-error";

export class DebugSource {
  constructor(private readonly client: FetchApiClientMethod) {}

  // 환자 screeningId로 join_guardian_patient를 조회해 보호자(UserGuardian)를 찾고, 해당 보호자와 연결된 모든 환자 계정을 완전 삭제한 뒤 보호자 계정을 완전 삭제합니다.
  async deleteGuardianWithPatient(screeningId: string): Promise<SATResult<void, SATError>> {
    const url = `${this.patientUrl(screeningId)}/guardian`;
    try {
      await this.client.delete(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(`[DebugSource] :: deleteGuardianWithPatient :: 보호자 하드 삭제 실패`, error);
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: deleteGuardianWithPatient | `, satError);
      return SATResult.failure(satError);
    }
  }

  // 환자(UserPatient)와 연관된 전체 데이터를 완전 삭제합니다.
  async deletePatientWithAllData(screeningId: string): Promise<SATResult<void, SATError>> {
    const url = this.patientUrl(screeningId);
    try {
      await this.client.delete(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(`[DebugSource] :: deletePatient :: 환자 하드 삭제 실패`, error);
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: deletePatient | `, satError);
      return SATResult.failure(satError);
    }
  }

  // 환자 계정(User/UserAuth/UserPatient)과 치료(TreatmentInfo/Plan)는 유지하고,
  // 나머지 연관 데이터를 삭제합니다(activity/calibration/친구/재화·아이템/미션/튜토리얼/스토리/로그/sync_status 등).
  async purgePatient(screeningId: string): Promise<SATResult<void, SATError>> {
    const url = `${this.patientUrl(screeningId)}/purge`;
    try {
      await this.client.delete(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(`[DebugSource] :: purgePatient :: 계정/치료 제외 환자 데이터 삭제 실패`, error);
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: purgePatient | `, satError);
      return SATResult.failure(satError);
    }
  }

  // 환자 활동 기록을 삭제합니다. yyyy-MM-dd
  async deleteActivity(
    screeningId: string,
    dateOfDay: string,
  ): Promise<SATResult<void, SATError>> {
    const url = `${this.patientUrl(screeningId)}/activity/${encodeURIComponent(dateOfDay)}`;
    try {
      await this.client.delete(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(`[DebugSource] :: deleteActivity :: 활동 기록 삭제 실패`, error);
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: deleteActivity | `, satError);
      return SATResult.failure(satError);
    }
  }

  // 환자 캘리브레이션 기록을 삭제합니다. yyyy-MM-dd
  async deleteCalibration(
    screeningId: string,
    dateOfDay: string,
  ): Promise<SATResult<void, SATError>> {
    const url = `${this.patientUrl(screeningId)}/calibration/${encodeURIComponent(dateOfDay)}`;
    try {
      await this.client.delete(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(`[DebugSource] :: deleteCalibration :: 날짜별 캘리브레이션 삭제 실패`, error);
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: deleteCalibration | `, satError);
      return SATResult.failure(satError);
    }
  }

  // 환자 친구 기록을 삭제합니다.
  async deleteFriends(screeningId: string): Promise<SATResult<void, SATError>> {
    const url = `${this.patientUrl(screeningId)}/friends`;
    try {
      await this.client.delete(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(`[DebugSource] :: deleteFriends :: 환자 친구 데이터 삭제 실패`, error);
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: deleteFriends | `, satError);
      return SATResult.failure(satError);
    }
  }

  // 환자 재화/아이템 기록을 삭제합니다.
  async deleteCurrencyItems(screeningId: string): Promise<SATResult<void, SATError>> {
    const url = `${this.patientUrl(screeningId)}/currency-items`;
    try {
      await this.client.delete(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(
        `[DebugSource] :: deleteCurrencyItems :: 환자 재화/아이템 데이터 삭제 실패`,
        error,
      );
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: deleteCurrencyItems | `, satError);
      return SATResult.failure(satError);
    }
  }

  // 환자 미션 기록을 삭제합니다.
  async deleteMissions(screeningId: string): Promise<SATResult<void, SATError>> {
    const url = `${this.patientUrl(screeningId)}/missions`;
    try {
      await this.client.delete(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(`[DebugSource] :: deleteMissions :: 환자 미션 로그 삭제 실패`, error);
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: deleteMissions | `, satError);
      return SATResult.failure(satError);
    }
  }

  // 환자 튜토리얼 기록을 삭제합니다.
  async deleteTutorial(screeningId: string): Promise<SATResult<void, SATError>> {
    const url = `${this.patientUrl(screeningId)}/tutorial`;
    try {
      await this.client.delete(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(`[DebugSource] :: deleteTutorial :: 환자 튜토리얼 진행도 삭제 실패`, error);
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: deleteTutorial | `, satError);
      return SATResult.failure(satError);
    }
  }

  // 환자 전체 튜토리얼 추가합니다.
  async addAllTutorials(screeningId: string): Promise<SATResult<void, SATError>> {
    const url = `${this.patientUrl(screeningId)}/tutorial`;
    try {
      await this.client.post(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(`[DebugSource] :: addAllTutorials :: 환자 전체 튜토리얼 추가 실패`, error);
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: addAllTutorials | `, satError);
      return SATResult.failure(satError);
    }
  }

  // 환자 스토리 기록을 삭제합니다.
  async deleteStory(screeningId: string): Promise<SATResult<void, SATError>> {
    const url = `${this.patientUrl(screeningId)}/story`;
    try {
      await this.client.delete(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(`[DebugSource] :: deleteStory :: 환자 스토리 진행도 삭제 실패`, error);
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: deleteStory | `, satError);
      return SATResult.failure(satError);
    }
  }

  // SN 만료 설정합니다.
  async setSnExpired(loginId: string): Promise<SATResult<void, SATError>> {
    const url = `${URLList.MAINTENANCE.SN_EXPIRE}/${encodeURIComponent(loginId)}`;
    try {
      await this.client.put(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(`[DebugSource] :: setSnExpired :: SN 만료 설정 실패`, error);
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: setSnExpired | `, satError);
      return SATResult.failure(satError);
    }
  }

  // 환자 light_estimation 데이터 수 설정합니다.
  // yyyy-MM-dd, count는 0~3
  async setLightEstimationCount(
    screeningId: string,
    dateOfDay: string,
    count: number,
  ): Promise<SATResult<void, SATError>> {
    const url = `${this.patientUrl(screeningId)}/light-estimation/${encodeURIComponent(
      dateOfDay,
    )}/${count}`;
    try {
      await this.client.put(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(
        `[DebugSource] :: setLightEstimationCount :: light_estimation 데이터 수 설정 실패`,
        error,
      );
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: setLightEstimationCount | `, satError);
      return SATResult.failure(satError);
    }
  }

  // 환자 활동 인덱스 설정합니다.
  // yyyy-MM-dd, sessionIndex는 0~5, cycleIndex는 0~4
  async setActivityIndex(
    screeningId: string,
    dateOfDay: string,
    sessionIndex: number,
    cycleIndex: number,
  ): Promise<SATResult<void, SATError>> {
    const url = `${this.patientUrl(screeningId)}/activity/${encodeURIComponent(
      dateOfDay,
    )}/${sessionIndex}/${cycleIndex}`;
    try {
      await this.client.put(url);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.error(`[DebugSource] :: setActivityIndex :: 활동 인덱스 설정 실패`, error);
      const satError = errorConverter.convertError(error);
      console.log(`[DebugSource] :: setActivityIndex | `, satError);
      return SATResult.failure(satError);
    }
  }

  private patientUrl(screeningId: string): string {
    return `${URLList.MAINTENANCE.PATIENT}/${encodeURIComponent(screeningId)}`;
  }
}
